import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { apiRequest, peekApiResponse } from "../../../../shared/api/client";
import { type SessionUser } from "../../../../shared/auth/access";
import {
  type ProcurementRevision,
} from "../../RequestDetail";
import { createRequestWorkspaceService } from "../../application/queries/request-workspace";
import { type RequestWorkspaceDetail } from "../../domain/models";
import type { OfferEvaluation } from "../../domain/offer-evaluation";
import { type ProcurementFilter } from "../../domain/selectors";
import { procurementQueryGateway } from "../../infrastructure/api/procurement-query-gateway";
import {
  compareProcurementRequests,
  type PurchaseRequest,
} from "../../model";
import { defaultRequestStageFilterId, requestStageFilterOptions } from "../../presentation/shared/request-stage-filters";
import { procurementOfferCorrectionFieldNames } from "../../../../../shared/procurement-correction-fields";

const requestWorkspaceService = createRequestWorkspaceService(
  procurementQueryGateway,
);

export type ApprovalsModuleProps = {
  notify: (message: string) => void;
  user: SessionUser;
  dataRefreshVersion: number;
  initialRequestNo?: string;
};

export function useApprovalsWorkspace({
  notify,
  user,
  dataRefreshVersion,
  initialRequestNo,
}: ApprovalsModuleProps) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(() =>
    defaultRequestStageFilterId(user.role),
  );
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(
    initialRequestNo ?? null,
  );
  const [closingRequestId, setClosingRequestId] = useState<string | null>(null);
  const modalCloseTimerRef = useRef<number | null>(null);
  const [workspaceRefreshVersion, setWorkspaceRefreshVersion] = useState(0);
  const [resolved, setResolved] = useState<Record<string, string>>({});
  const [pendingDecision, setPendingDecision] = useState<{
    requestId: string;
    action: "Reddedildi" | "Uzmana geri gönder";
  } | null>(null);
  const [decisionNote, setDecisionNote] = useState("");
  const [decisionNoteManuallyEdited, setDecisionNoteManuallyEdited] =
    useState(false);
  const [decisionError, setDecisionError] = useState("");
  const [decisionSubmitting, setDecisionSubmitting] = useState(false);
  const [revisionFields, setRevisionFields] = useState<string[]>([]);
  const [gmFinalOfferSelections, setGmFinalOfferSelections] = useState<
    Record<string, string>
  >({});
  const [gmFinalDecisionNotes, setGmFinalDecisionNotes] = useState<
    Record<string, string>
  >({});
  const [gmOverrideRequestId, setGmOverrideRequestId] = useState<string | null>(
    null,
  );
  const [rows, setRows] = useState<PurchaseRequest[]>(() => {
    const cached = peekApiResponse<PurchaseRequest[]>("/api/requests") ?? [];
    const options = requestStageFilterOptions(user.role);
    return cached.filter((request) =>
      options.some((option) => option.matches(request)),
    );
  });
  const [evaluations, setEvaluations] = useState<
    Record<string, OfferEvaluation>
  >({});
  const [revisionsByRequest, setRevisionsByRequest] = useState<
    Record<string, ProcurementRevision[]>
  >({});
  const [workspaceByRequest, setWorkspaceByRequest] = useState<
    Record<string, RequestWorkspaceDetail>
  >({});
  const [loading, setLoading] = useState(
    () => !peekApiResponse<PurchaseRequest[]>("/api/requests"),
  );
  const [returnedDecisions, setReturnedDecisions] = useState<
    Array<{
      id: string;
      requestNo: string;
      title: string;
      fields: string[];
      instruction: string;
      requestedBy: string;
      requestedAt: string;
    }>
  >([]);
  const [forwardingReturnId, setForwardingReturnId] = useState<string | null>(
    null,
  );
  const closeExpandedRequest = useCallback(() => {
    if (!expandedRequestId || closingRequestId === expandedRequestId) return;
    setClosingRequestId(expandedRequestId);
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    modalCloseTimerRef.current = window.setTimeout(
      () => {
        setExpandedRequestId(null);
        setClosingRequestId(null);
        modalCloseTimerRef.current = null;
      },
      reduceMotion ? 0 : 220,
    );
  }, [closingRequestId, expandedRequestId]);
  useEffect(
    () => () => {
      if (modalCloseTimerRef.current !== null) {
        window.clearTimeout(modalCloseTimerRef.current);
      }
    },
    [],
  );
  useEffect(() => {
    if (!expandedRequestId) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeExpandedRequest();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [closeExpandedRequest, expandedRequestId]);
  useEffect(() => {
    if (!expandedRequestId) return;
    let active = true;
    Promise.all([
      apiRequest<{ revisions?: ProcurementRevision[]; }>(
        `/api/requests/${encodeURIComponent(expandedRequestId)}`,
      ),
      requestWorkspaceService.load(expandedRequestId),
    ])
      .then(([detail, workspace]) => {
        if (!active) return;
        setRevisionsByRequest((current) => ({
          ...current,
          [expandedRequestId]: detail.revisions ?? [],
        }));
        setWorkspaceByRequest((current) => ({
          ...current,
          [expandedRequestId]: workspace,
        }));
      })
      .catch(() => {
        if (!active) return;
        setRevisionsByRequest((current) => ({
          ...current,
          [expandedRequestId]: [],
        }));
      });
    return () => {
      active = false;
    };
  }, [expandedRequestId, workspaceRefreshVersion]);
  const revisionFieldOptions = procurementOfferCorrectionFieldNames.filter(
    (field) => field !== "Tedarikçi veya teklif belgesi",
  );
  const assignedApprovalRole =
    user.role === "SATIN_ALMA_MUDURU" || user.role === "GENEL_MUDUR"
      ? user.role
      : null;
  const stageOptions = requestStageFilterOptions(user.role);
  const selectedStageOption = stageOptions.find(
    (option) => option.id === statusFilter,
  );
  const visibleRows = rows.filter(
    (request) =>
      `${request.id} ${request.title} ${request.requester} ${request.department}`
        .toLocaleLowerCase("tr")
        .includes(query.trim().toLocaleLowerCase("tr")) &&
      (!selectedStageOption || selectedStageOption.matches(request)),
  );
  const showReturnedQueue =
    user.role === "SATIN_ALMA_MUDURU" && statusFilter === "manager-returned";
  const returnedRequestNos = new Set(
    returnedDecisions.map((item) => item.requestNo),
  );
  const orderedVisibleRows = [
    ...(user.role === "SATIN_ALMA_MUDURU" && !showReturnedQueue
      ? visibleRows.filter((request) => !returnedRequestNos.has(request.id))
      : visibleRows),
  ].sort((left, right) => {
    if (left.id === initialRequestNo) return -1;
    if (right.id === initialRequestNo) return 1;
    return compareProcurementRequests(left, right);
  });
  const orderedReturnedDecisions = [...returnedDecisions].sort(
    (left, right) => {
      if (left.requestNo === initialRequestNo) return -1;
      if (right.requestNo === initialRequestNo) return 1;
      return 0;
    },
  );
  const closeDecisionComposer = () => {
    setPendingDecision(null);
    setGmOverrideRequestId(null);
    setDecisionNote("");
    setDecisionNoteManuallyEdited(false);
    setDecisionError("");
    setRevisionFields([]);
    setGmFinalOfferSelections({});
    setGmFinalDecisionNotes({});
  };
  const closeGmOverrideComposer = () => {
    setGmOverrideRequestId(null);
    setDecisionError("");
  };
  const selectGmFinalOffer = (requestId: string, offerId: string) => {
    setGmFinalOfferSelections((current) => ({
      ...current,
      [requestId]: offerId,
    }));
    setGmOverrideRequestId(null);
    setDecisionError("");
    setGmFinalDecisionNotes((current) => {
      const next = { ...current };
      delete next[requestId];
      return next;
    });
  };
  const openDecisionComposer = (
    requestId: string,
    action: "Reddedildi" | "Uzmana geri gönder",
  ) => {
    setDecisionNote("");
    setDecisionNoteManuallyEdited(false);
    setDecisionError("");
    setRevisionFields([]);
    setPendingDecision({ requestId, action });
  };
  const stageCount = (option: ProcurementFilter) => {
    const requestIds = new Set(
      rows.filter(option.matches).map((request) => request.id),
    );
    if (option.id === "manager-returned") {
      returnedDecisions.forEach((item) => requestIds.add(item.requestNo));
    }
    return requestIds.size;
  };
  useEffect(() => {
    setQuery("");
    setStatusFilter(defaultRequestStageFilterId(user.role));
    setResolved({});
    setPendingDecision(null);
    setDecisionNote("");
    setRevisionFields([]);
    setExpandedRequestId(initialRequestNo ?? null);
  }, [initialRequestNo, user.role]);
  useEffect(() => {
    let active = true;
    const cachedRequests = peekApiResponse<PurchaseRequest[]>("/api/requests");
    const cachedReturns =
      user.role === "SATIN_ALMA_MUDURU"
        ? peekApiResponse<typeof returnedDecisions>("/api/corrections")
        : [];
    if (cachedRequests) {
      const options = requestStageFilterOptions(user.role);
      setRows(
        cachedRequests.filter((request) =>
          options.some((option) => option.matches(request)),
        ),
      );
    }
    if (cachedReturns) setReturnedDecisions(cachedReturns);
    setLoading(!cachedRequests);
    Promise.all([
      apiRequest<PurchaseRequest[]>("/api/requests"),
      user.role === "SATIN_ALMA_MUDURU"
        ? apiRequest<
          Array<{
            id: string;
            requestNo: string;
            title: string;
            fields: string[];
            instruction: string;
            requestedBy: string;
            requestedAt: string;
          }>
        >("/api/corrections")
        : Promise.resolve([]),
    ])
      .then(async ([data, managerReturns]) => {
        if (active) {
          setReturnedDecisions(managerReturns);
          const options = requestStageFilterOptions(user.role);
          const workspaceRequests = data.filter((request) =>
            options.some((option) => option.matches(request)),
          );
          setRows(workspaceRequests);
          setStatusFilter((current) => {
            const focusedRequest = initialRequestNo
              ? workspaceRequests.find(
                (request) => request.id === initialRequestNo,
              )
              : null;
            const focusedOption = focusedRequest
              ? options.find((option) => option.matches(focusedRequest))
              : null;
            if (focusedOption) return focusedOption.id;
            return current ?? defaultRequestStageFilterId(user.role);
          });
          const evaluationRows = await Promise.all(
            workspaceRequests.map(async (item) => {
              const evaluation = await apiRequest<OfferEvaluation>(
                `/api/requests/${item.id}/evaluations/latest`,
              ).catch(() => null);
              return [item.id, evaluation] as const;
            }),
          );
          if (active) {
            setEvaluations(
              Object.fromEntries(
                evaluationRows.filter(
                  (entry): entry is readonly [string, OfferEvaluation] =>
                    entry[1] !== null,
                ),
              ),
            );
          }
        }
      })
      .catch(() => {
        if (!cachedRequests) notify("Onay kayıtları sunucudan alınamadı.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [
    dataRefreshVersion,
    initialRequestNo,
    notify,
    user.role,
    workspaceRefreshVersion,
  ]);
  const forwardReturnedDecision = async (returnRecord: {
    id: string;
    requestNo: string;
    fields: string[];
    instruction: string;
  }) => {
    setForwardingReturnId(returnRecord.id);
    try {
      await apiRequest(`/api/corrections/${returnRecord.id}/delegate`, {
        method: "POST",
        body: JSON.stringify({
          fields: returnRecord.fields,
          instruction: returnRecord.instruction,
        }),
      });
      setReturnedDecisions((current) =>
        current.filter((item) => item.id !== returnRecord.id),
      );
      notify(`${returnRecord.requestNo} Satın Alma Uzmanına yönlendirildi.`);
      setWorkspaceRefreshVersion((current) => current + 1);
    } catch (error) {
      notify(
        error instanceof Error
          ? error.message
          : "Reddedilen dosya uzmana yönlendirilemedi.",
      );
    } finally {
      setForwardingReturnId(null);
    }
  };
  const act = async (
    request: PurchaseRequest,
    action: "Onaylandı" | "Reddedildi" | "Uzmana geri gönder",
    note = "",
    selectedOfferId?: string,
  ) => {
    if (decisionSubmitting) return;
    setDecisionSubmitting(true);
    setDecisionError("");
    try {
      await apiRequest(`/api/approvals/${request.id}`, {
        method: "POST",
        body: JSON.stringify({
          action,
          note,
          revisionFields:
            action === "Uzmana geri gönder" || action === "Reddedildi"
              ? revisionFields
              : undefined,
          selectedOfferId: action === "Onaylandı" ? selectedOfferId : undefined,
        }),
      });
      setResolved((current) => ({ ...current, [request.id]: action }));
      closeDecisionComposer();
      notify(
        action === "Onaylandı" && user.role === "SATIN_ALMA_MUDURU"
          ? `${request.id} Genel Müdüre iletildi.`
          : action === "Reddedildi"
            ? `${request.id} Satın Alma Müdürüne döndü.`
            : action === "Uzmana geri gönder"
              ? `${request.id} için düzeltme görevi gönderildi.`
              : note.trim()
                ? `${request.id} için nihai tedarikçi seçimi gerekçesiyle değiştirildi.`
                : `${request.id} nihai olarak onaylandı.`,
      );
      setWorkspaceRefreshVersion((current) => current + 1);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Karar kaydedilemedi. Lütfen bağlantınızı kontrol edip yeniden deneyin.";
      setDecisionError(message);
      notify(message);
    } finally {
      setDecisionSubmitting(false);
    }
  };
  return {
    user,
    initialRequestNo,
    query,
    setQuery,
    statusFilter,
    setStatusFilter,
    expandedRequestId,
    setExpandedRequestId,
    closingRequestId,
    setClosingRequestId,
    resolved,
    pendingDecision,
    decisionNote,
    setDecisionNote,
    decisionNoteManuallyEdited,
    setDecisionNoteManuallyEdited,
    decisionError,
    setDecisionError,
    decisionSubmitting,
    revisionFields,
    setRevisionFields,
    gmFinalOfferSelections,
    gmFinalDecisionNotes,
    setGmFinalDecisionNotes,
    gmOverrideRequestId,
    setGmOverrideRequestId,
    rows,
    evaluations,
    revisionsByRequest,
    workspaceByRequest,
    loading,
    returnedDecisions,
    forwardingReturnId,
    closeExpandedRequest,
    revisionFieldOptions,
    assignedApprovalRole,
    stageOptions,
    visibleRows,
    showReturnedQueue,
    orderedVisibleRows,
    orderedReturnedDecisions,
    closeDecisionComposer,
    closeGmOverrideComposer,
    selectGmFinalOffer,
    openDecisionComposer,
    stageCount,
    forwardReturnedDecision,
    act,
  };
}
